import * as Styled from './ScansList.styles';

import { GatesManagement } from 'features/GatesManagement/GatesManagement';
import { Icon } from 'features/common/components/Icon';
import { IdParamsType } from 'utils/types';
import { MainHeader } from 'features/common/components/MainHeader';
import { MainLayout } from 'layouts/MainLayout';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { ROUTES } from 'features/routes';
import { RootState } from 'store';
import { Table } from 'features/common/components/Table';
import { useDebounce } from 'utils/utils';
import styled from 'styled-components';
import { useGetEventScans } from 'hooks/useGetEventScans';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { DateTime } from 'luxon';

const COLUMN_HEADINGS = ['Full Name', 'Email', 'Gate', 'Scanned Quantity', 'Last Scan Date'];
const COLUMN_MIN_WIDTH = [150, 200, 80, 120, 160];
const GRID_COLUMNS =
	'minmax(150px, 1fr) minmax(200px, 1.5fr) minmax(80px, 0.5fr) minmax(120px, 0.8fr) minmax(160px, 1fr)';

const GATE_GROUP_COLUMNS = 'minmax(250px, 2fr) minmax(120px, 0.5fr) minmax(160px, 1fr)';
const GATE_GROUP_HEADINGS = ['Scanner Info', 'Scanned Quantity', 'Last Scan Date'];
const GATE_GROUP_MIN_WIDTH = [250, 120, 160];

type TabType = 'scans' | 'gates';
type ViewType = 'flat' | 'byGate';

const CustomFlexContainer = styled.div`
	width: 100%;
	padding: 24px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 20px;
	background: ${({ theme }) => theme.colors.black};
`;

const ControlsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const RefreshButton = styled.button`
	background: transparent;
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
	color: ${({ theme }) => theme.colors.whitePearl};
	padding: 8px;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	position: relative;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}11;
	}

	&:active {
		background: ${({ theme }) => theme.colors.whitePearl}22;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;

		&:hover {
			background: transparent;
		}

		&::after {
			content: attr(data-tooltip);
			position: absolute;
			bottom: 100%;
			left: 50%;
			transform: translateX(-50%);
			padding: 4px 8px;
			background: ${({ theme }) => theme.colors.black};
			color: ${({ theme }) => theme.colors.whitePearl};
			font-size: 12px;
			border-radius: 4px;
			white-space: nowrap;
			opacity: 0;
			visibility: hidden;
			transition: opacity 0.2s ease, visibility 0.2s ease;
			pointer-events: none;
			margin-bottom: 8px;
			border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
		}

		&:hover::after {
			opacity: 1;
			visibility: visible;
		}
	}

	.icon {
		transition: transform 0.2s ease;
	}

	&:not(:disabled):hover .icon {
		transform: rotate(30deg);
	}

	&:not(:disabled):active .icon {
		transform: rotate(180deg);
	}
`;

const LastUpdatedText = styled.span`
	color: ${({ theme }) => theme.colors.whitePearl}77;
	font-size: 13px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const GroupingSelector = styled.div`
	display: flex;
	align-items: center;
	background: ${({ theme }) => theme.colors.black};
	border-radius: 20px;
	padding: 4px;
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
`;

const GroupButton = styled.button<{ active: boolean }>`
	background: ${({ active, theme }) => (active ? theme.colors.whitePearl : 'transparent')};
	color: ${({ active, theme }) => (active ? theme.colors.black : theme.colors.whitePearl)};
	border: none;
	padding: 8px 16px;
	border-radius: 16px;
	cursor: pointer;
	font-size: 13px;
	transition: all 0.2s ease;
	white-space: nowrap;

	&:hover {
		background: ${({ active, theme }) => (!active ? theme.colors.whitePearl + '11' : theme.colors.whitePearl)};
	}
`;

const GateHeader = styled.div`
	background: ${({ theme }) => theme.colors.black};
	padding: 16px 24px;
	margin: 0;
	font-weight: 500;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}11;
	min-width: fit-content;
`;

const GateTitle = styled.span`
	font-size: 15px;
	font-weight: 500;
`;

const GateStats = styled.div`
	display: flex;
	align-items: center;
	gap: 32px;
	margin-left: 32px;
`;

const StatItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	align-items: flex-end;
	min-width: 120px;
`;

const StatLabel = styled.span`
	font-size: 11px;
	color: ${({ theme }) => theme.colors.whitePearl}77;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

const StatValue = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.whitePearl};
`;

const TableContainer = styled(Table.TableContainer)`
	margin: 0;
	background: ${({ theme }) => theme.colors.black};
	overflow-x: auto;
	width: 100%;

	table {
		min-width: fit-content;
		width: 100%;
	}

	thead {
		tr {
			background: ${({ theme }) => theme.colors.black};
			border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}22;
		}

		th {
			font-size: 13px;
			font-weight: 500;
			color: ${({ theme }) => theme.colors.whitePearl}99;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			padding: 12px 16px;
			white-space: nowrap;
		}
	}

	tbody tr {
		background: ${({ theme }) => theme.colors.black};
		border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}11;

		&:hover {
			background: ${({ theme }) => theme.colors.whitePearl}11;
		}

		td {
			padding: 12px 16px;
			white-space: nowrap;
		}
	}
`;

const ScannerInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const ScannerName = styled.span`
	font-weight: 500;
	font-size: 14px;
`;

const ScannerEmail = styled.span`
	font-size: 13px;
	color: ${({ theme }) => theme.colors.whitePearl}77;
`;

const SearchContainer = styled.div`
	position: relative;
	width: 300px;

	.search-icon {
		position: absolute;
		left: 12px;
		top: 50%;
		transform: translateY(-50%);
		color: ${({ theme }) => theme.colors.whitePearl}77;
	}

	input {
		width: 100%;
		padding: 8px 12px 8px 36px;
		border-radius: 20px;
		border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
		background: transparent;
		color: ${({ theme }) => theme.colors.whitePearl};
		font-size: 13px;

		&::placeholder {
			color: ${({ theme }) => theme.colors.whitePearl}77;
		}

		&:focus {
			outline: none;
			border-color: ${({ theme }) => theme.colors.whitePearl}66;
		}
	}
`;

const formatDate = (dateString: string | Date) => {
	const date = typeof dateString === 'string' ? DateTime.fromISO(dateString) : DateTime.fromJSDate(dateString);
	return date.toFormat('MM/dd/yyyy hh:mm:ss a');
};

export const ScansList = () => {
	const { id } = useParams<IdParamsType>();
	const { isPunchup } = useSelector((state: RootState) => state.auth);
	const { push } = useHistory();
	const theme = useTheme();
	const [activeTab, setActiveTab] = useState<TabType>('scans');
	const [viewType, setViewType] = useState<ViewType>('flat');
	const [searchText, setSearchText] = useState('');
	const [lastUpdated, setLastUpdated] = useState(() => {
		const stored = localStorage.getItem('scansListLastUpdated');
		return stored ? new Date(stored) : new Date();
	});
	const [remainingTime, setRemainingTime] = useState(0);
	const [selectedScanner, setSelectedScanner] = useState<string | null>(null);
	const searchDebounce = useDebounce(setSearchText);

	// Gate/scanner configuration is not offered to customers inside the Punchup iframe (PUN-4942 req. 6),
	// matching what TIC-308 already did in the green view. The standalone dark-theme site keeps it.
	const showGatesManagement = !isPunchup;
	const pageTitle = showGatesManagement ? 'Scans & Gates Management' : 'Scanned Tickets';

	const { data, isLoading, refetch } = useGetEventScans();

	useEffect(() => {
		const updateRemainingTime = () => {
			const now = new Date();
			const timeSinceLastRefresh = now.getTime() - lastUpdated.getTime();
			const remaining = Math.ceil((2 * 60 * 1000 - timeSinceLastRefresh) / 1000);
			setRemainingTime(Math.max(0, remaining));
		};

		// Update immediately
		updateRemainingTime();

		// Update every second
		const interval = setInterval(updateRemainingTime, 1000);

		return () => clearInterval(interval);
	}, [lastUpdated]);

	const isRefreshDisabled = () => {
		return remainingTime > 0 || isLoading;
	};

	const getRefreshTooltip = () => {
		if (isLoading) return 'Refreshing...';

		if (remainingTime > 0) {
			const minutes = Math.floor(remainingTime / 60);
			const seconds = remainingTime % 60;
			return `Please wait ${minutes}m ${seconds}s before refreshing again`;
		}

		return '';
	};

	const handleRefresh = async () => {
		const newDate = new Date();
		setLastUpdated(newDate);
		localStorage.setItem('scansListLastUpdated', newDate.toISOString());
		await refetch();
	};

	const handleRowClick = (email: string) => {
		if (selectedScanner === email) {
			setSelectedScanner(null); // Deseleccionar si ya estaba seleccionado
		} else {
			setSelectedScanner(email);
		}
	};

	const getFilteredData = () => {
		if (!data) return [];

		let filtered = data;

		if (searchText) {
			filtered = filtered.filter(
				(scan) =>
					scan.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
					scan.email.toLowerCase().includes(searchText.toLowerCase()) ||
					scan.gateName.toLowerCase().includes(searchText.toLowerCase())
			);
		}

		if (selectedScanner) {
			filtered = filtered.filter((scan) => scan.email === selectedScanner);
		}

		return filtered;
	};

	const getScansGroupedByGate = () => {
		if (!data) return [];

		const filteredData = searchText
			? data.filter(
					(scan) =>
						scan.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
						scan.email.toLowerCase().includes(searchText.toLowerCase()) ||
						scan.gateName.toLowerCase().includes(searchText.toLowerCase())
			  )
			: data;

		const groupedScans = filteredData.reduce((acc, scan) => {
			const gate = scan.gateName;
			if (!acc[gate]) {
				acc[gate] = {
					gateName: gate,
					totalScans: 0,
					uniqueScanners: new Set(),
					lastScanDate: new Date(0),
					scans: [],
				};
			}

			acc[gate].scans.push(scan);
			acc[gate].totalScans += scan.scannedQuantity;
			acc[gate].uniqueScanners.add(scan.email);
			const scanDate = new Date(scan.lastScanDate);
			if (scanDate > acc[gate].lastScanDate) {
				acc[gate].lastScanDate = scanDate;
			}

			return acc;
		}, {} as Record<string, { gateName: string; totalScans: number; uniqueScanners: Set<string>; lastScanDate: Date; scans: typeof data }>);

		return Object.values(groupedScans)
			.map((group) => ({
				...group,
				uniqueScannersCount: group.uniqueScanners.size,
			}))
			.sort((a, b) => b.totalScans - a.totalScans);
	};

	if (isLoading) {
		const loadingBody = (
			<>
				<MainHeader lessPadding gap="12px">
					{!isPunchup && (
						<Icon
							cursor="pointer"
							iconName="arrowLeft"
							onClick={() => push(generatePath(ROUTES.DASHBOARD_PATH, { id }))}
						/>
					)}
					<MainHeader.Title element="h1">{pageTitle}</MainHeader.Title>
				</MainHeader>
				<Styled.Container>
					<div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
				</Styled.Container>
			</>
		);
		if (isPunchup) {
			return <PunchupPageShell pageName="Tickets Scanned">{loadingBody}</PunchupPageShell>;
		}
		return <MainLayout>{loadingBody}</MainLayout>;
	}

	const groupedScans = getScansGroupedByGate();
	const filteredData = getFilteredData();

	const mainBody = (
		<>
			<MainHeader lessPadding gap="12px">
				{!isPunchup && (
					<Icon
						cursor="pointer"
						iconName="arrowLeft"
						onClick={() => push(generatePath(ROUTES.DASHBOARD_PATH, { id }))}
					/>
				)}
				<MainHeader.Title element="h1">{pageTitle}</MainHeader.Title>
			</MainHeader>

			<Styled.Container>
				{showGatesManagement && (
					<Styled.TabsContainer>
						<Styled.TabButton active={activeTab === 'scans'} onClick={() => setActiveTab('scans')}>
							Scans List
						</Styled.TabButton>
						<Styled.TabButton active={activeTab === 'gates'} onClick={() => setActiveTab('gates')}>
							Gates Management
						</Styled.TabButton>
					</Styled.TabsContainer>
				)}

				{activeTab === 'scans' && (
					<>
						<CustomFlexContainer>
							<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
								<SearchContainer>
									<Icon iconName="search" size="M" className="search-icon" />
									<input
										type="text"
										onChange={(e) => searchDebounce(e.target.value)}
										placeholder="Search by name, email or gate..."
									/>
								</SearchContainer>
								<LastUpdatedText>
									<Icon iconName="greenClock" size="M" />
									Updated {formatDate(lastUpdated)}
								</LastUpdatedText>
							</div>
							<ControlsContainer>
								<RefreshButton
									onClick={handleRefresh}
									disabled={isRefreshDisabled()}
									data-tooltip={getRefreshTooltip()}
								>
									<Icon iconName="refresh" size="M" className="icon" />
								</RefreshButton>
								<GroupingSelector>
									<GroupButton active={viewType === 'flat'} onClick={() => setViewType('flat')}>
										Flat View
									</GroupButton>
									<GroupButton active={viewType === 'byGate'} onClick={() => setViewType('byGate')}>
										Group by Gate
									</GroupButton>
								</GroupingSelector>
							</ControlsContainer>
						</CustomFlexContainer>

						{selectedScanner && (
							<div
								style={{
									padding: '12px 24px',
									background: theme.colors.whitePearl + '11',
									margin: '0 24px',
									borderRadius: '8px',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<div>
									<div style={{ fontSize: '15px', fontWeight: 500 }}>Showing scans for scanner:</div>
									<div style={{ color: theme.colors.whitePearl + '99', fontSize: '13px' }}>{selectedScanner}</div>
								</div>
								<button
									onClick={() => setSelectedScanner(null)}
									style={{
										background: 'transparent',
										border: 'none',
										color: theme.colors.whitePearl,
										cursor: 'pointer',
										padding: '8px',
										borderRadius: '4px',
										display: 'flex',
										alignItems: 'center',
										gap: '4px',
										fontSize: '13px',
									}}
								>
									<Icon iconName="close" size="M" />
									Clear filter
								</button>
							</div>
						)}

						{viewType === 'flat' ? (
							<TableContainer>
								<Table>
									<thead>
										<Table.HeaderRow gridColumns={GRID_COLUMNS}>
											{COLUMN_HEADINGS.map((heading, i) => (
												<Table.Cell key={heading} minWidth={COLUMN_MIN_WIDTH[i]}>
													<Table.ColumnHeader>{heading}</Table.ColumnHeader>
												</Table.Cell>
											))}
										</Table.HeaderRow>
									</thead>
									<tbody>
										{filteredData.map((scan, index) => (
											<Table.DataRow
												gridColumns={GRID_COLUMNS}
												key={`${scan.userId || index}-${scan.gate}-${index}`}
												onClick={() => handleRowClick(scan.email)}
												style={{
													cursor: 'pointer',
													background: selectedScanner === scan.email ? theme.colors.whitePearl + '22' : undefined,
												}}
											>
												<Table.Cell minWidth={COLUMN_MIN_WIDTH[0]}>{scan.fullName}</Table.Cell>
												<Table.Cell minWidth={COLUMN_MIN_WIDTH[1]}>{scan.email}</Table.Cell>
												<Table.Cell minWidth={COLUMN_MIN_WIDTH[2]}>{scan.gateName}</Table.Cell>
												<Table.Cell minWidth={COLUMN_MIN_WIDTH[3]}>{scan.scannedQuantity}</Table.Cell>
												<Table.Cell minWidth={COLUMN_MIN_WIDTH[4]}>{formatDate(scan.lastScanDate)}</Table.Cell>
											</Table.DataRow>
										))}
									</tbody>
								</Table>
							</TableContainer>
						) : (
							<div>
								{groupedScans.map((gateGroup) => (
									<div key={gateGroup.gateName}>
										<GateHeader>
											<GateTitle>{gateGroup.gateName}</GateTitle>
											<GateStats>
												<StatItem>
													<StatLabel>Total Scans</StatLabel>
													<StatValue>{gateGroup.totalScans}</StatValue>
												</StatItem>
												<StatItem>
													<StatLabel>Unique Scanners</StatLabel>
													<StatValue>{gateGroup.uniqueScannersCount}</StatValue>
												</StatItem>
												<StatItem>
													<StatLabel>Last Activity</StatLabel>
													<StatValue>{formatDate(gateGroup.lastScanDate)}</StatValue>
												</StatItem>
											</GateStats>
										</GateHeader>
										<TableContainer>
											<Table>
												<thead>
													<Table.HeaderRow gridColumns={GATE_GROUP_COLUMNS}>
														{GATE_GROUP_HEADINGS.map((heading, i) => (
															<Table.Cell key={heading} minWidth={GATE_GROUP_MIN_WIDTH[i]}>
																<Table.ColumnHeader>{heading}</Table.ColumnHeader>
															</Table.Cell>
														))}
													</Table.HeaderRow>
												</thead>
												<tbody>
													{gateGroup.scans.map((scan, index) => (
														<Table.DataRow
															gridColumns={GATE_GROUP_COLUMNS}
															key={`${scan.userId || index}-${scan.gate}-${index}`}
														>
															<Table.Cell minWidth={GATE_GROUP_MIN_WIDTH[0]}>
																<ScannerInfo>
																	<ScannerName>{scan.fullName}</ScannerName>
																	<ScannerEmail>{scan.email}</ScannerEmail>
																</ScannerInfo>
															</Table.Cell>
															<Table.Cell minWidth={GATE_GROUP_MIN_WIDTH[1]}>{scan.scannedQuantity}</Table.Cell>
															<Table.Cell minWidth={GATE_GROUP_MIN_WIDTH[2]}>
																{formatDate(scan.lastScanDate)}
															</Table.Cell>
														</Table.DataRow>
													))}
												</tbody>
											</Table>
										</TableContainer>
									</div>
								))}
							</div>
						)}
					</>
				)}
				{showGatesManagement && activeTab === 'gates' && <GatesManagement />}
			</Styled.Container>
		</>
	);

	if (isPunchup) {
		return <PunchupPageShell pageName="Tickets Scanned">{mainBody}</PunchupPageShell>;
	}
	return <MainLayout>{mainBody}</MainLayout>;
};
